import type { ReactNode } from "react";
import { DesktopHeader } from "@/app/layouts/DesktopHeader";
import type { DesktopLayoutEvent } from "@/app/layouts/desktopLayoutMediator";
import { MainContent } from "@/app/layouts/layoutAccessibility";
import { useDesktopLayoutPresenter } from "@/app/layouts/useDesktopLayoutPresenter";
import { DesktopAnnouncementModal } from "@/features/announce/components/DesktopAnnouncementModal";
import { DesktopAnnouncementModalContext } from "@/features/announce/hooks/useDesktopAnnouncementModal";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";

interface Props {
  children: ReactNode;
}

/** 旧AI_BBS PC版の余白と固定ヘッダーを再現するデスクトップ枠。 */
export const DesktopLayout: React.FunctionComponent<Props> = ({
  children,
}: Props) => {
  const presentation = useDesktopLayoutPresenter();
  return (
    <ComponentScope node={presentation.node}>
      <DesktopLayoutView {...presentation} onEvent={presentation.node.dispatch}>
        {children}
      </DesktopLayoutView>
    </ComponentScope>
  );
};

interface DesktopLayoutViewProps
  extends Props,
    Omit<ReturnType<typeof useDesktopLayoutPresenter>, "node"> {
  onEvent: (event: DesktopLayoutEvent) => void;
}

export function DesktopLayoutView({
  children,
  isPcForced,
  density,
  headerFixed,
  replyButtonFixed,
  announcementModalRequest,
  announcementModalController,
  onEvent,
}: DesktopLayoutViewProps): ReactNode {
  return (
    <DesktopAnnouncementModalContext value={announcementModalController}>
      <div
        className="desktop-app min-h-screen bg-background text-foreground"
        data-force-pc={isPcForced || undefined}
        data-density={density}
        data-header-fixed={headerFixed || undefined}
        data-reply-button-flow={!replyButtonFixed || undefined}
      >
        <DesktopHeader />
        <MainContent className="desktop-main">{children}</MainContent>
        {announcementModalRequest && (
          <DesktopAnnouncementModal
            isOpen
            initialAnnouncementId={announcementModalRequest.announcementId}
            onClose={(): void =>
              onEvent({ type: "desktop-layout.announcement.close" })
            }
          />
        )}
      </div>
    </DesktopAnnouncementModalContext>
  );
}
